import type { PortraitViewRow } from "@/lib/characters-api"
import { characterListThumbnail } from "@/lib/character-thumbnail"
import type { LocationFacing } from "@/lib/locations-api"
import { locationListThumbnail } from "@/lib/location-thumbnail"
import type { ShotReferenceInputRow } from "@/lib/shot-reference-inputs-api"
import i18n from "@/i18n"
import type { MentionItem } from "./types"

export type CastRow = {
  id: number
  name: string
  role?: string | null
  reference_sheet_url?: string | null
  reference_sheet_thumb_url?: string | null
  reference_display_url?: string | null
  /** Full same-origin proxy for sketch / attach (not display variant). */
  reference_download_url?: string | null
  /** Blob id matching download_url priority (sheet → ref → portrait). */
  attach_blob_id?: number | null
  character_sheet_url?: string | null
  character_sheet_display_url?: string | null
  character_sheet_download_url?: string | null
  portrait_views?: PortraitViewRow[]
  portrait_thumb_url?: string | null
  portrait_url?: string | null
  portrait_download_url?: string | null
  active_voice_id?: number | null
  voice_ready?: boolean
  voice_provider_kind?: string | null
  effective_height_cm?: number
  height_explicit?: boolean
  /** A-pose attached — needed for scale-sheet figure completeness. */
  has_scale_pose?: boolean
}

export type LocationRow = {
  id: number
  name: string
  setting_type?: string | null
  reference_sheet_url?: string | null
  reference_sheet_download_url?: string | null
  /** Blob id of the primary-facing original (same file as candidate download_url). */
  attach_blob_id?: number | null
  reference_urls_by_facing?: Partial<Record<LocationFacing, string | null>>
  reference_thumb_urls_by_facing?: Partial<Record<LocationFacing, string | null>>
  reference_download_urls_by_facing?: Partial<Record<LocationFacing, string | null>>
}

export type PropStateRow = {
  id: number
  name: string
  reference_url?: string | null
  /** Full-size / download URL when reference_url is a thumb. */
  reference_full_url?: string | null
  /** Same-origin proxy for sketch (preferred over reference_full_url). */
  reference_download_url?: string | null
  attach_blob_id?: number | null
}

export type PropRow = {
  id: number
  name: string
  category?: string | null
  reference_sheet_url?: string | null
  reference_display_url?: string | null
  reference_download_url?: string | null
  front_view_url?: string | null
  front_view_thumb_url?: string | null
  front_view_download_url?: string | null
  /** Blob id matching download_url priority (front view → reference sheet). */
  attach_blob_id?: number | null
  thumb_url?: string | null
  has_front_view?: boolean
  effective_height_cm?: number
  height_explicit?: boolean
  states?: PropStateRow[]
}

function propMentionThumb(p: PropRow): { imageUrl: string | null; frontViewChrome: boolean } {
  const front = p.front_view_thumb_url?.trim() || null
  if (front) return { imageUrl: front, frontViewChrome: true }
  const thumb = p.thumb_url?.trim() || p.reference_sheet_url?.trim() || null
  return { imageUrl: thumb, frontViewChrome: false }
}

export type LinkedPropAssignment = {
  prop_id: number
  state_id?: number | null
}

export type CameraMentionRow = {
  id: number
  name: string
  /** Постер пресета; в чип кладём только реальное изображение, не demo video. */
  imageUrl?: string | null
}

type Sources = {
  characters?: CastRow[]
  locations?: LocationRow[]
  props?: PropRow[]
  references?: ShotReferenceInputRow[]
  cameras?: CameraMentionRow[]
  /** Id героев, привязанных к шоту (поднимаются в верхний блок). */
  linkedCharacterIds?: number[]
  /** Id локаций, привязанных к шоту (поднимаются в верхний блок). */
  linkedLocationIds?: number[]
  /** Привязки реквизита к шоту (prop_id + опциональный state_id). */
  linkedPropAssignments?: LinkedPropAssignment[]
}

export function propMentionLabel(name: string, stateName?: string | null): string {
  const state = stateName?.trim()
  return state ? `${name} (${state})` : name
}

function propPinned(assignment: LinkedPropAssignment | undefined): boolean {
  return assignment != null
}

function referenceSubtitle(mediaType: ShotReferenceInputRow["media_type"]): string | null {
  if (mediaType === "video") return i18n.t("shot:promptEditor.videoMedia")
  if (mediaType === "audio" || mediaType === "voice") return i18n.t("shot:promptEditor.audioMedia")
  return null
}

function referenceMentionMediaType(mediaType: ShotReferenceInputRow["media_type"]): MentionItem["mediaType"] {
  if (mediaType === "video") return "video"
  if (mediaType === "audio" || mediaType === "voice") return "audio"
  return "image"
}

/** Единый список меншенов для редактора промпта из данных проекта и шота. */
export function buildMentionItems({
  characters,
  locations,
  props,
  references,
  cameras,
  linkedCharacterIds,
  linkedLocationIds,
  linkedPropAssignments,
}: Sources): MentionItem[] {
  const items: MentionItem[] = []
  const linkedChars = new Set(linkedCharacterIds ?? [])
  const linkedLocs = new Set(linkedLocationIds ?? [])
  const linkedPropsById = new Map((linkedPropAssignments ?? []).map((a) => [a.prop_id, a]))

  for (const c of characters ?? []) {
    items.push({
      category: "hero",
      id: c.id,
      label: c.name,
      subtitle: c.role || null,
      imageUrl: characterListThumbnail(c),
      pinned: linkedChars.has(c.id),
    })
  }

  for (const l of locations ?? []) {
    items.push({
      category: "loc",
      id: l.id,
      label: l.name,
      subtitle: l.setting_type || null,
      imageUrl: locationListThumbnail(l),
      pinned: linkedLocs.has(l.id),
    })
  }

  for (const p of props ?? []) {
    const linked = linkedPropsById.get(p.id)
    const baseThumb = propMentionThumb(p)
    items.push({
      category: "prop",
      id: p.id,
      stateId: null,
      label: p.name,
      subtitle: p.category || i18n.t("shot:promptEditor.propBase"),
      imageUrl: baseThumb.imageUrl,
      frontViewChrome: baseThumb.frontViewChrome,
      pinned: propPinned(linked),
    })
    for (const state of p.states ?? []) {
      const stateUrl = state.reference_url?.trim() || null
      items.push({
        category: "prop",
        id: p.id,
        stateId: state.id,
        label: propMentionLabel(p.name, state.name),
        subtitle: null,
        imageUrl: stateUrl ?? baseThumb.imageUrl,
        frontViewChrome: stateUrl ? false : baseThumb.frontViewChrome,
        pinned: propPinned(linked),
      })
    }
  }

  for (const r of references ?? []) {
    const mediaType = referenceMentionMediaType(r.media_type)
    items.push({
      category: "reference",
      id: r.position,
      label: r.label,
      subtitle: referenceSubtitle(r.media_type),
      mediaType,
      imageUrl: mediaType === "video" || mediaType === "audio" ? null : (r.thumb_url ?? r.url),
    })
  }

  for (const cam of cameras ?? []) {
    items.push({
      category: "cam",
      id: cam.id,
      label: cam.name,
      imageUrl: cam.imageUrl || null,
    })
  }

  return items
}
